import { Link } from '@truepill/tpos-react-router'
import { ReactComponent as SplitIcon } from 'assets/icons/git-branch.svg'
import IconWrapper from 'components/IconWrapper'
import { ModalHeader, ModalWrapper } from 'components/Modal'
import Lozenge, { getStatusColor } from 'components/Tiles/Lozenge'
import { useTPCacheContext } from 'providers/TPCacheProvider'
import { goToFulfillmentOrder } from 'routes'
import styled, { css } from 'styled-components'
import { bodyPrimaryColor, linkColor } from 'styles/styleVariables'
import type { SplitLinkedOrder } from 'types'

type SplitOrdersModalProps = {
  // The original, when this order is a split-out order
  splitFromOrder?: SplitLinkedOrder
  splitOutOrders: SplitLinkedOrder[]
}

const SplitOrdersModal = ({ splitFromOrder, splitOutOrders }: SplitOrdersModalProps): JSX.Element => (
  <ModalWrapper id="SplitOrdersModal" styles={modalWidth}>
    <ModalHeader>
      <IconWrapper>
        <SplitIcon fill={bodyPrimaryColor} />
      </IconWrapper>
      <h2>Split orders</h2>
    </ModalHeader>
    {splitFromOrder && (
      <Section data-testid="split-from">
        <h3>Split from</h3>
        <SplitOrderRow order={splitFromOrder} />
      </Section>
    )}
    {splitOutOrders.length > 0 && (
      <Section data-testid="split-to">
        <h3>Split to</h3>
        {splitOutOrders.map(order => (
          <SplitOrderRow key={order._id} order={order} />
        ))}
      </Section>
    )}
  </ModalWrapper>
)

const SplitOrderRow = ({ order }: { order: SplitLinkedOrder }): JSX.Element => {
  const { locationsById } = useTPCacheContext()

  return (
    <Row data-testid="split-order-row">
      <OrderLink to={goToFulfillmentOrder({ orderId: order._id })} target="_blank" rel="noopener noreferrer">
        Order {order.coreOrderToken}
      </OrderLink>
      <LocationName>{locationsById[order.locationId]?.name ?? ''}</LocationName>
      <Lozenge backgroundColor={getStatusColor(order.status)}>{order.status}</Lozenge>
    </Row>
  )
}

const modalWidth = css`
  min-width: 24rem;
  max-width: 36rem;
`

const Section = styled.section`
  margin-top: 0.75rem;
  > h3 {
    font-weight: 500;
    margin-bottom: 0.25rem;
  }
`

const Row = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 0.75rem;
  align-items: center;
  padding: 0.375rem 0;
`

const OrderLink = styled(Link)`
  text-decoration: underline;
  :hover {
    color: ${linkColor};
  }
`

const LocationName = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export default SplitOrdersModal
